import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnInit,
  afterEveryRender,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { dateTimeFormats } from '@shared/constants/dateTimeFormats';
import { HD_JUST_NOW_ICONS, HD_RAVE_NEW_YEAR_ICON } from '../../chart/library/hd-chart-cycle-metadata';
import { HdGateLineComponent } from '../../chart/bodygraph/gate-info/line/hd-gate-line.component';
import { HdGateLineViewModel } from '../../chart/bodygraph/gate-info/line/hd-gate-line.view-model';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../chart/bodygraph/hd-gate-info.service';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { HdPlanetInfoService } from '../../chart/bodygraph/hd-planet-info.service';
import {
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON,
  HdPlanetKey,
  getHdPlanetDisplayConfig,
} from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdChartSettingsStore } from '../../chart/bodygraph/chart-tabs/hd-chart-settings.store';
import { IChingLinePosition } from '../../iching/core/iching.models';
import { HdIChingAccessService } from '../../iching/hd-iching-access.service';
import { HdIChingDayTarget, HdIChingGatePoint } from '../../iching/panel/hd-iching-panel.models';
import { HdIChingPanelService } from '../../iching/panel/hd-iching-panel.service';
import { HD_EPHEMERIS_DOMAIN_MAX_MS, HD_EPHEMERIS_DOMAIN_MIN_MS } from '../ephemeris-lanes/hd-ephemeris-lanes.utils';
import { hdSeasonBySunGate } from '../../chart/mandala/hd-season-markers';
import { HdTransitShiftPopupService } from '../shift-popup/hd-transit-shift-popup.service';
import { HdTransitCountdownKey, formatGateLine, selectCountdownFormat } from '../transit-countdown.utils';
import { HdTransitGateLineVmFactory, createHdTransitGateLineVmFactory } from '../transit-gate-line-vm.factory';
import { HdTransitMomentAddRequest } from '../transit-moment-add.models';
import { HdTransitGateInfoMode, HdTransitPlanetSegments, HdTransitSegment } from '../transit.models';
import { HdTransitsViewStateService } from '../transits-view-state.service';
import {
  HdTransitDay,
  HdTransitDayShift,
  HdTransitDaySeason,
  HdTransitDayStation,
  buildHdTransitDay,
  hdTransitDayKey,
} from './hd-transit-day-flow.builder';
import {
  HdTransitDayFlowMetrics,
  HdTransitDayWindow,
  applyHdTransitDayWindowStep,
  hdTransitDayWindowKeys,
  hdTransitFocusDayKey,
  hdTransitInitialDayWindow,
  planHdTransitDayWindowStep,
} from './hd-transit-day-window';
import { HdTransitScrollActivity } from './hd-transit-scroll-activity';

const I18N_PREFIX = 'humanDesign.chart.transits.vertical.dayFlow';
const MS_PER_MINUTE = 60_000;
/**
 * Een "vandaag"-label langer dan dit (NL "vandaag", niet EN "today") past niet
 * in de smalle rail van het rechterpaneel en krijgt daar een kleiner font.
 */
const RAIL_TODAY_MAX_CHARS = 5;
/** Zolang na het laatste scroll-event (of het loslaten) geldt de gebruiker nog als scrollend. */
const SCROLL_IDLE_MS = 180;
/** Minimale ruimte tussen de rail (in rust) en een plusje dat eronder moest zakken. */
const RAIL_ADD_CLEARANCE_PX = 4;
/** Nu-sprong: de nu-lijn komt op dit deel van de hoogte (de lopende stand erboven blijft deels zichtbaar). */
const NOW_JUMP_VIEWPORT_FRACTION = 1 / 3;

/** Countdown-formaat → duur-key ("2d 4u" i.p.v. "nog 2d 4u"). */
const DURATION_KEYS: Readonly<Record<HdTransitCountdownKey, string>> = {
  countdownMinutes: 'durationMinutes',
  countdownHours: 'durationHours',
  countdownDays: 'durationDays',
  countdownMonths: 'durationMonths',
};

/** Grenzen van de ephemeris-dekking als dagsleutels (verder scrollen kan niet). */
const DOMAIN_BOUNDS = {
  minKey: hdTransitDayKey(HD_EPHEMERIS_DOMAIN_MIN_MS),
  maxKey: hdTransitDayKey(HD_EPHEMERIS_DOMAIN_MAX_MS),
};

interface DayFlowPositionVm {
  readonly planetKey: HdPlanetKey;
  readonly glyph: string;
  readonly color: string;
  readonly label: string;
  readonly retrograde: boolean;
  readonly tooltip: string;
}

/** Poortinfo van de nieuwe stand: poortbadge (keynotes) of poortrij (keynotes + I Tjing). */
interface DayFlowGateVm {
  readonly gate: number;
  readonly hexagram: string;
  readonly keynote1: string;
  readonly keynote2: string;
  readonly description: string;
  /** Tooltip/aria van de doorklik naar de volledige gate-info. */
  readonly openLabel: string;
}

interface DayFlowShiftVm {
  readonly type: 'shift';
  readonly id: string;
  readonly kind: 'gate' | 'line';
  readonly timeText: string;
  readonly planetName: string;
  readonly glyph: string;
  readonly color: string;
  readonly retrograde: boolean;
  readonly previousText: string | null;
  readonly currentText: string;
  readonly nextText: string | null;
  readonly durationText: string;
  /** "Kwartaal van … begint" wanneer de zon hier een Sphinx-poort ingaat; anders null. */
  readonly quarterStartText: string | null;
  /** Rave New Year (de zon gaat poort 41 in): het jaar + de tooltip van de badge; anders null. */
  readonly raveNewYear: { readonly year: number; readonly tooltip: string } | null;
  readonly untilTooltip: string;
  readonly gateLinkText: string;
  readonly gateVm: DayFlowGateVm;
  /** I Tjing-lijnsectie van de nieuwe stand; `null` bij een zuiver poort-segment. */
  readonly lineVm: HdGateLineViewModel | null;
  readonly source: HdTransitDayShift;
}

interface DayFlowStationVm {
  readonly type: 'station';
  readonly id: string;
  readonly timeText: string;
  readonly planetName: string;
  readonly glyph: string;
  readonly color: string;
  readonly text: string;
}

/** Zonnewende of equinox als eigen regel (lecture 2026-10-21, analyse 4C). */
interface DayFlowSeasonVm {
  readonly type: 'season';
  readonly id: string;
  readonly timeText: string;
  /** Font Awesome-naam (solid) van het seizoensicoon. */
  readonly icon: string;
  readonly text: string;
  readonly detail: string;
}

type DayFlowEntryVm = DayFlowShiftVm | DayFlowStationVm | DayFlowSeasonVm;

interface DayFlowDayVm {
  readonly key: string;
  readonly startMs: number;
  readonly isToday: boolean;
  /** De data van minstens één zichtbare planeet is voor deze dag (nog) niet binnen. */
  readonly pending: boolean;
  /** Nog helemaal niets bekend: de dag staat er als placeholder van ongeveer een schermhoogte. */
  readonly placeholder: boolean;
  readonly weekday: string;
  readonly dayNumber: string;
  readonly monthLabel: string;
  readonly ariaLabel: string;
  readonly positions: readonly DayFlowPositionVm[];
  readonly entries: readonly DayFlowEntryVm[];
  /** Tijdstip (epoch-ms) per entry, voor de nu-lijn. */
  readonly entryTimes: readonly number[];
}

/** Dag + offset binnen die dag: het punt dat bovenaan het beeld hoort te staan. */
interface DayFlowAnchor {
  readonly key: string;
  readonly offset: number;
}

type GateVmFactory = (gate: number) => DayFlowGateVm;

/**
 * De eigenlijke dag-flow van de verticale transits-weergave (spec §2.5-2.6):
 * onder elkaar de kalenderdagen, per dag een agenda-achtige datumrail, de
 * stand van de planeten om 00:00 en daaronder elke poort- of lijnwissel als
 * blok (tijdstip, planeet, vorige → nieuwe → volgende stand, hoelang de nieuwe
 * stand actief blijft) met de I Tjing-lijnsectie van de nieuwe stand.
 *
 * **Venster + treadmill (optie C).** Er staat altijd maar een handvol dagen in
 * de DOM (`window`); de pure beslisregels in `hd-transit-day-window` laten
 * aan de scrollkant één dag bijkomen en ver weggescrolde dagen écht
 * verdwijnen. Geen zichtbare scrollbalk: de weekstrip en de maandkalender van
 * de host zijn de positie-indicator. Scroll-, touch- en resize-listeners lopen
 * buiten de Angular-zone; alles gaat per animation frame.
 *
 * **Bedoeld anker.** `anchor` (dag + offset) is het punt dat bovenaan het
 * beeld hoort; het verandert enkel door een sprong of doordat de gebruiker
 * zelf scrolt. Na elke inhoudswijziging zet de component de scroll terug op
 * dat anker. BELANGRIJK: het anker wordt nooit afgeleid uit een scrollpositie
 * die de component zelf zette — een geklemde scroll (inhoud korter dan het
 * beeld) zou het anker anders telkens een dag laten wegdrijven. Zie de spec
 * "geen anker-drift".
 *
 * **Niets boven het beeld tijdens het scrollen.** Zolang de gebruiker scrolt
 * (vinger op het scherm, of momentum) komen er boven het beeld geen dagen bij
 * of af, en nieuwe data wordt pas toegepast als het scrollen stilvalt
 * ({@link HdTransitScrollActivity}). Zo hoeft de component tijdens het
 * scrollen nooit zelf de scrollpositie te zetten.
 */
@Component({
  selector: 'hd-transit-day-flow',
  imports: [HdGateLineComponent, NgTemplateOutlet],
  templateUrl: './hd-transit-day-flow.component.html',
  styleUrl: './hd-transit-day-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.day-flow-host--compact]': 'compact()',
  },
})
export class HdTransitDayFlowComponent implements OnInit {
  /**
   * Segmenten van de zichtbare planeten, in weergavevolgorde (dagkop +
   * gelijktijdige wissels). Een planeet met lege segmenten is nog aan het laden.
   */
  readonly planets = input.required<readonly HdTransitPlanetSegments[]>();
  readonly todayKey = input.required<string>();
  /** Dag bovenaan bij de start; wordt één keer gelezen (sprongen lopen via {@link jumpTo}). */
  readonly initialFocusKey = input.required<string>();
  /** Offset binnen de startdag (herstel na een undock/dock-cyclus). */
  readonly initialOffsetPx = input(0);
  /** Huidig tijdstip (epoch-ms) voor de nu-lijn; de host levert het op minuutresolutie. */
  readonly nowMs = input(Date.now());
  readonly compact = input(false);
  /** Poortinfo bij de wissels: enkel de poortlink, een badge met de keynotes, of een extra poortrij. */
  readonly gateInfoMode = input<HdTransitGateInfoMode>('link');
  /** "Nieuwe poort"-badge bij een poortwissel (uit in het smalle rechterpaneel). */
  readonly gateShiftBadge = input(true);
  /** Just Now-knop op de nu-lijn; uit zodra de nu-lijn een gepinde kijktijd volgt (tijdslider). */
  readonly justNowAvailable = input(false);

  /** De dag bovenaan het beeld veranderde (weekstrip + kalender volgen). */
  readonly focusDayChanged = output<string>();
  /** Het gerenderde venster veranderde (de host laadt de data errond). */
  readonly windowChanged = output<HdTransitDayWindow>();
  readonly momentAddRequested = output<HdTransitMomentAddRequest>();
  /** Just Now-knop op de nu-lijn: de host opent de transit-chart van nu. */
  readonly justNowRequested = output<void>();
  /** Rave New Year-badge: de host opent de transit-chart van dat jaar. */
  readonly raveNewYearRequested = output<number>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly popupService = inject(HdTransitShiftPopupService);
  private readonly viewState = inject(HdTransitsViewStateService);
  private readonly ichingAccess = inject(HdIChingAccessService);
  private readonly ichingPanel = inject(HdIChingPanelService);
  private readonly chartSettings = inject(HdChartSettingsStore);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');

  protected readonly justNowIcon = HD_JUST_NOW_ICONS.transit;
  protected readonly raveNewYearIcon = HD_RAVE_NEW_YEAR_ICON;

  /** De scroll-plusjes bestaan enkel met Classic I Ching-toegang (feature én setting). */
  protected readonly ichingAvailable = computed(() => this.ichingAccess.classicVisible());

  /** Het "hexagram van de dag"-icoon in de dagkop: toegang én de gebiedssetting voor transits. */
  protected readonly ichingIconsVisible = computed(
    () => this.ichingAvailable() && this.chartSettings.classicIChingIconsTransits(),
  );

  /**
   * Per gerenderde dag de zon- en maanstand om 12:00 lokaal uit de toegepaste
   * planeten (HD-laag, spec 2026-10-18): zonder zon in de legende geen icoon;
   * de maan is optioneel; bij gate-granulariteit ontbreekt de lijn.
   */
  protected readonly ichingDays = computed<ReadonlyMap<string, Pick<HdIChingDayTarget, 'sun' | 'moon'>>>(() => {
    const planets = this.appliedPlanets();
    const sun = planets.find(planet => planet.planetKey === 'sun');
    const moon = planets.find(planet => planet.planetKey === 'moon');
    const result = new Map<string, Pick<HdIChingDayTarget, 'sun' | 'moon'>>();
    if (!sun) return result;
    for (const day of this.dayVms()) {
      const noonMs = day.startMs + 12 * 60 * MS_PER_MINUTE;
      const sunPoint = gatePointAt(sun.segments, noonMs);
      if (!sunPoint) continue;
      const moonPoint = moon ? gatePointAt(moon.segments, noonMs) : undefined;
      result.set(day.key, moonPoint ? { sun: sunPoint, moon: moonPoint } : { sun: sunPoint });
    }
    return result;
  });

  /** Hexagram van deze dag (zon, maan, Chinese dagteller) in het Classic I Ching-paneel. */
  protected openIChingDay(day: DayFlowDayVm, event: MouseEvent): void {
    const points = this.ichingDays().get(day.key);
    if (!points) return;
    this.ichingPanel.openDay({ view: 'day', dateKey: day.key, ...points }, { x: event.clientX, y: event.clientY });
  }

  private readonly activity = new HdTransitScrollActivity(SCROLL_IDLE_MS);

  /** Gerenderde dagen; `null` tot ngOnInit de startdag kent. */
  private readonly window = signal<HdTransitDayWindow | null>(null);

  /**
   * De getoonde segmenten: volgen `planets`, maar niet zolang de gebruiker
   * scrolt — nieuwe data verandert de hoogte van dagen boven het beeld, en die
   * verschuiving mag pas gecompenseerd worden als het scrollen stilvalt.
   */
  private readonly appliedPlanets = linkedSignal<
    readonly HdTransitPlanetSegments[],
    readonly HdTransitPlanetSegments[]
  >({
    source: this.planets,
    computation: (planets, previous) =>
      previous == null || this.activity.isIdle(Date.now()) ? planets : previous.value,
  });

  private anchor: DayFlowAnchor | null = null;
  /** Een Nu-sprong waarvan het anker nog opnieuw bepaald wordt (zie {@link resolveNowAnchor}). */
  private nowJumpPending = false;
  private lastFocusKey: string | null = null;
  /**
   * De scrollpositie die de component zelf zette (na klemming). Zolang de
   * scroller daar staat, is het bedoelde anker de waarheid; wijkt hij af, dan
   * scrolde de gebruiker en telt zijn positie.
   */
  private ownedScrollTop: number | null = null;
  /** De scroller is zichtbaar en zijn positie is sinds het tonen hersteld. */
  private visible = false;
  private userScrollSinceFrame = false;
  private contentDirty = false;
  private resizePending = false;
  private lastClientHeight = 0;
  private frame: number | null = null;
  private momentumFrame: number | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  /** Dag-VM's per dagsleutel, geldig zolang {@link dayVmCacheDeps} ongewijzigd is. */
  private dayVmCache = new Map<string, DayFlowDayVm>();
  private dayVmCacheDeps: readonly unknown[] = [];

  private readonly lang = computed(() => {
    // Meelezen zodat de eerste translate ná een async translation-load opnieuw
    // evalueert (zie HdContentLanguageService.translationsLoadedTick).
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly text = computed(() => {
    const lang = this.lang();
    const translate = (key: string) => this.transloco.translate<string>(`${I18N_PREFIX}.${key}`, undefined, lang);
    const today = translate('today');
    return {
      flowAria: translate('flowAria'),
      today,
      todayLong: today.length > RAIL_TODAY_MAX_CHARS,
      startPositions: translate('startPositions'),
      noShifts: translate('noShifts'),
      loadingDay: translate('loadingDay'),
      openDetails: translate('openDetails'),
      addMoment: translate('addMoment'),
      addMomentDay: translate('addMomentDay'),
      addMomentNow: translate('addMomentNow'),
      justNow: translate('justNow'),
      justNowTooltip: translate('justNowTooltip'),
      raveNewYear: translate('raveNewYear'),
      ichingDay: translate('ichingDay'),
      previous: translate('previousLabel'),
      next: translate('nextLabel'),
      now: translate('now'),
      retrograde: translate('retrograde'),
      gateShift: translate('gateShift'),
      gateCaption: translate('gateCaption'),
    };
  });

  private readonly planetNames = computed<Readonly<Record<string, string>>>(() => {
    const lang = this.lang();
    return Object.fromEntries(
      HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.map(config => [
        config.key,
        this.planetInfo.planetInfoIn(config.key, lang)?.name || config.label,
      ]),
    );
  });

  /**
   * Bouwer van de I Tjing-lijnsectie per `poort.lijn` + activerende planeet,
   * met een cache per taal/scope-load (de computed herevalueert bij elke
   * translation-load, zodat de cache nooit lege pre-load-teksten vasthoudt).
   * Het lijn-VM behoudt zo zijn identiteit tussen renders: `hd-gate-line`
   * (OnPush) rendert niet opnieuw wanneer het venster verschuift.
   */
  private readonly lineVmFactory = computed<HdTransitGateLineVmFactory>(() =>
    createHdTransitGateLineVmFactory({
      lang: this.lang(),
      gateInfo: this.gateInfo,
      planetInfo: this.planetInfo,
      transloco: this.transloco,
    }),
  );

  /**
   * Poortinfo (keynotes + korte I Tjing-beschrijving) per poort, met een cache
   * per taal/scope-load zoals {@link lineVmFactory}: alle wissels binnen
   * dezelfde poort delen hetzelfde VM.
   */
  private readonly gateVmFactory = computed<GateVmFactory>(() => {
    const lang = this.lang();
    const cache = new Map<number, DayFlowGateVm>();
    return (gate: number) => {
      const cached = cache.get(gate);
      if (cached != null) return cached;
      const info = this.gateInfo.gateInfoIn(gate, lang);
      const vm: DayFlowGateVm = {
        gate,
        hexagram: info.metadata.ichingChar,
        keynote1: info.translations.keynote1,
        keynote2: info.translations.keynote2,
        description: info.translations.shortIchingDescription,
        openLabel: this.transloco.translate<string>(`${I18N_PREFIX}.gateInfoOpen`, { gate }, lang),
      };
      cache.set(gate, vm);
      return vm;
    };
  });

  /**
   * De dagen van het venster. Per dag gememoïseerd zolang data, taal en
   * vandaag gelijk blijven: een treadmill-stap bouwt enkel de nieuwe dag, en
   * de bestaande dagen houden hun VM-identiteit (geen her-render).
   */
  protected readonly dayVms = computed<readonly DayFlowDayVm[]>(() => {
    const window = this.window();
    if (window == null) return [];
    const planets = this.appliedPlanets();
    const todayKey = this.todayKey();
    const lang = this.lang();
    const lineVm = this.lineVmFactory();
    const gateVm = this.gateVmFactory();
    const names = this.planetNames();
    const translate = (key: string, params?: Record<string, unknown>) =>
      this.transloco.translate<string>(`${I18N_PREFIX}.${key}`, params, lang);

    const deps: readonly unknown[] = [planets, todayKey, lang, lineVm, gateVm, names];
    const reusable = deps.every((dep, index) => dep === this.dayVmCacheDeps[index]) ? this.dayVmCache : null;
    const next = new Map<string, DayFlowDayVm>();
    const vms = hdTransitDayWindowKeys(window).map(key => {
      const vm = reusable?.get(key)
        ?? this.toDayVm(buildHdTransitDay(key, planets, todayKey), names, lineVm, gateVm, translate, momentLocaleFor(lang));
      next.set(key, vm);
      return vm;
    });
    this.dayVmCache = next;
    this.dayVmCacheDeps = deps;
    return vms;
  });

  /** Plaats van de nu-lijn: vóór de eerste entry van vandaag die nog moet komen. */
  protected readonly nowMarker = computed<{ readonly dayKey: string; readonly index: number; readonly time: string } | null>(() => {
    const now = this.nowMs();
    const today = this.dayVms().find(day => day.isToday);
    if (today == null) return null;
    const index = today.entryTimes.findIndex(time => time > now);
    return {
      dayKey: today.key,
      index: index < 0 ? today.entries.length : index,
      time: moment(now).format('HH:mm'),
    };
  });

  constructor() {
    // Het gerenderde venster melden: de host laadt er de segmenten rond.
    effect(() => {
      const window = this.window();
      if (window != null) untracked(() => this.windowChanged.emit(window));
    });

    // Vóór elke inhoudswijziging: heeft de gebruiker sinds de laatste eigen
    // scroll-zet gescrold, dan wordt zijn positie het anker. Een
    // component-effect draait tijdens change detection vóór de
    // template-refresh: de DOM toont hier nog de oude inhoud.
    // Ook een wissel van de poortinfo-modus verandert de hoogte van alle dagen
    // (ook die boven het beeld): zonder die meelezing verschoof de inhoud
    // onder de gebruiker weg in plaats van op het anker te blijven staan.
    effect(() => {
      this.dayVms();
      this.nowMarker();
      this.gateInfoMode();
      this.gateShiftBadge();
      this.justNowAvailable();
      untracked(() => {
        this.contentDirty = true;
        this.captureUserAnchor();
      });
    });

    // Na de render: het anker terugzetten (een dag erbij of eraf boven het
    // beeld, nieuwe data) en het venster opnieuw toetsen.
    afterEveryRender(() => this.onAfterRender());

    effect(onCleanup => {
      const element = this.scroller()?.nativeElement;
      if (element == null) return;
      const onScroll = (): void => this.onScroll(element);
      const onTouchStart = (): void => this.activity.touchStart();
      const onTouchEnd = (): void => {
        this.activity.touchEnd(Date.now());
        this.scheduleIdleCheck();
      };
      let observer: ResizeObserver | null = null;
      this.ngZone.runOutsideAngular(() => {
        element.addEventListener('scroll', onScroll, { passive: true });
        element.addEventListener('touchstart', onTouchStart, { passive: true });
        element.addEventListener('touchend', onTouchEnd, { passive: true });
        element.addEventListener('touchcancel', onTouchEnd, { passive: true });
        if (typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(() => this.onResize());
          observer.observe(element);
        }
      });
      onCleanup(() => {
        element.removeEventListener('scroll', onScroll);
        element.removeEventListener('touchstart', onTouchStart);
        element.removeEventListener('touchend', onTouchEnd);
        element.removeEventListener('touchcancel', onTouchEnd);
        observer?.disconnect();
      });
    });

    this.destroyRef.onDestroy(() => {
      this.cancelScheduled();
      const anchor = this.anchor;
      if (anchor != null) {
        this.viewState.saveVerticalFlowState({ focusKey: anchor.key, offsetPx: Math.max(0, anchor.offset) });
      }
    });
  }

  ngOnInit(): void {
    const focusKey = this.initialFocusKey();
    this.anchor = { key: focusKey, offset: this.initialOffsetPx() };
    this.lastFocusKey = focusKey;
    // Een nieuwe scroller staat bovenaan: die positie is nog niet van de gebruiker.
    this.ownedScrollTop = 0;
    this.window.set(hdTransitInitialDayWindow(focusKey));
  }

  /**
   * Springt naar een dag (weekstrip, kalender, Vandaag). De doeldag wordt het
   * anker en meteen de focusdag. Staat hij al in het venster, dan volstaat
   * scrollen; anders wordt het venster hard rond de doeldag herbouwd — geen
   * doorscrollen over weken inhoud.
   */
  jumpTo(dayKey: string): void {
    this.jump(dayKey, false);
  }

  /**
   * Springt naar het nu-moment (Nu-knop): naar vandaag, met de nu-lijn op een
   * derde van de hoogte in plaats van de dagstart bovenaan. Met de maan aan
   * staan er ±15 wissels per dag en ligt de nu-lijn vaak ver onder de dagstart.
   */
  jumpToNow(): void {
    this.jump(untracked(this.todayKey), true);
  }

  private jump(dayKey: string, toNow: boolean): void {
    this.nowJumpPending = toNow;
    this.activity.reset();
    this.userScrollSinceFrame = false;
    this.anchor = { key: dayKey, offset: 0 };
    this.emitFocus();
    const latest = untracked(this.planets);
    if (untracked(this.appliedPlanets) !== latest) this.appliedPlanets.set(latest);

    const element = this.scroller()?.nativeElement ?? null;
    if (element != null) {
      this.stopMomentum(element);
      // Tot de gebruiker zelf scrolt, is de doeldag het anker — niet wat er nu in beeld staat.
      this.ownedScrollTop = element.scrollTop;
    }
    if (this.sectionFor(dayKey) == null) {
      this.window.set(hdTransitInitialDayWindow(dayKey));
      return;
    }
    if (element != null && this.visible) this.restoreAnchor(element);
    this.requestFrame();
  }

  /** Klik op een wissel → de gedeelde detail-popup (info-card + planeetkaart), zoals op de lanes. */
  protected openShiftDetails(entry: DayFlowShiftVm, event: MouseEvent): void {
    const shift = entry.source;
    const planet = this.appliedPlanets().find(candidate => candidate.planetKey === shift.planetKey);
    // Keert de planeet binnen deze stand, dan geldt ℞ niet voor de hele stand: de popup toont dan het station.
    const stations = (planet?.stations ?? []).filter(
      station => station.atUtc.valueOf() >= shift.atMs && station.atUtc.isBefore(shift.untilUtc),
    );
    this.popupService.open(
      {
        planetId: shift.planetId,
        planetKey: shift.planetKey,
        gate: shift.current.gate,
        line: shift.current.line,
        fromUtc: shift.atUtc,
        toUtc: shift.untilUtc,
        retrograde: shift.retrograde && stations.length === 0,
        stations,
        segments: planet?.segments ?? [],
      },
      { x: event.clientX, y: event.clientY },
    );
  }

  /**
   * Poortlink → de volledige gate-info met de transiterende planeet als
   * context, met dezelfde plaatsing als de detail-popups van de transits
   * (dock volgens de app-instelling; zwevend in de volle transits-dialog).
   */
  protected openGateInfo(entry: DayFlowShiftVm, event: MouseEvent): void {
    const shift = entry.source;
    this.popupService.openGateInfo(
      shift.current.gate,
      { planetId: shift.planetId, planetKey: shift.planetKey, gate: shift.current.gate, line: shift.current.line },
      { x: event.clientX, y: event.clientY },
    );
  }

  /**
   * Moment op het tijdstip van een wissel. BELANGRIJK: op de eerste volle
   * minuut ná de wissel — de momentdialoog rekent op minuutprecisie, en op de
   * afgekapte minuut (14:32 voor een wissel om 14:32:47) staat de planeet nog
   * in de vórige stand. Zie de day-flow-spec "eerste volle minuut".
   */
  protected requestShiftMoment(entry: DayFlowShiftVm): void {
    const minuteAfter = Math.ceil(entry.source.atMs / MS_PER_MINUTE) * MS_PER_MINUTE;
    this.momentAddRequested.emit({ atUtc: moment.utc(minuteAfter), withTime: true });
  }

  /** Moment voor de hele dag (zonder uur → dagrange in de dialoog). */
  protected requestDayMoment(day: DayFlowDayVm): void {
    this.momentAddRequested.emit({ atUtc: moment.utc(day.startMs), withTime: false });
  }

  /** Moment op het nu-moment (plusje op de nu-lijn). */
  protected requestNowMoment(): void {
    this.momentAddRequested.emit({ atUtc: moment.utc(this.nowMs()), withTime: true });
  }

  /** Rave New Year-badge: de transit-chart van dat jaar (de klik opent niet ook de wisseldetails). */
  protected requestRaveNewYear(entry: DayFlowShiftVm, event: MouseEvent): void {
    event.stopPropagation();
    if (entry.raveNewYear != null) this.raveNewYearRequested.emit(entry.raveNewYear.year);
  }

  // ── VM-bouw ──────────────────────────────────────────────────────────────

  private toDayVm(
    day: HdTransitDay,
    names: Readonly<Record<string, string>>,
    lineVm: HdTransitGateLineVmFactory,
    gateVm: GateVmFactory,
    translate: (key: string, params?: Record<string, unknown>) => string,
    locale: string,
  ): DayFlowDayVm {
    // Datumnamen in de content-taal: de globale moment-locale volgt een taalwissel niet.
    const date = moment(day.startMs).locale(locale);
    return {
      key: day.key,
      startMs: day.startMs,
      isToday: day.isToday,
      pending: !day.complete,
      placeholder: !day.complete && day.startPositions.length === 0 && day.entries.length === 0,
      weekday: date.format('dd'),
      dayNumber: date.format('D'),
      monthLabel: date.year() === moment().year() ? date.format('MMM') : date.format('MMM YYYY'),
      ariaLabel: date.format('dddd D MMMM YYYY'),
      positions: day.startPositions.map(position => {
        const config = getHdPlanetDisplayConfig(position.planetKey);
        const label = formatGateLine(position.gate, position.line);
        const name = names[position.planetKey] ?? config.label;
        return {
          planetKey: position.planetKey,
          glyph: config.astronomiconChar,
          color: config.stripeColor,
          label,
          retrograde: position.retrograde,
          tooltip: position.retrograde ? `${name} ${label} ℞` : `${name} ${label}`,
        };
      }),
      entries: day.entries.map(entry =>
        entry.type === 'shift'
          ? this.toShiftVm(entry, names, lineVm, gateVm, translate, locale)
          : entry.type === 'station'
            ? this.toStationVm(entry, names, translate)
            : this.toSeasonVm(entry, translate),
      ),
      entryTimes: day.entries.map(entry => entry.atMs),
    };
  }

  private toShiftVm(
    shift: HdTransitDayShift,
    names: Readonly<Record<string, string>>,
    lineVm: HdTransitGateLineVmFactory,
    gateVm: GateVmFactory,
    translate: (key: string, params?: Record<string, unknown>) => string,
    locale: string,
  ): DayFlowShiftVm {
    const config = getHdPlanetDisplayConfig(shift.planetKey);
    const duration = selectCountdownFormat(shift.durationMs);
    return {
      type: 'shift',
      id: shift.id,
      kind: shift.kind,
      timeText: moment(shift.atMs).format('HH:mm'),
      planetName: names[shift.planetKey] ?? config.label,
      glyph: config.astronomiconChar,
      color: config.stripeColor,
      retrograde: shift.retrograde,
      previousText: shift.previous != null ? formatGateLine(shift.previous.gate, shift.previous.line) : null,
      currentText: formatGateLine(shift.current.gate, shift.current.line),
      nextText: shift.next != null ? formatGateLine(shift.next.gate, shift.next.line) : null,
      durationText: translate('activeFor', {
        duration: translate(DURATION_KEYS[duration.key], duration.params),
      }),
      quarterStartText: shift.quarterStart != null
        ? translate('quarterStart', { quarter: this.mandalaText(`quarters.${shift.quarterStart.key}.title`) })
        : null,
      raveNewYear: shift.raveNewYear != null
        ? {
          year: shift.raveNewYear,
          tooltip: translate('raveNewYearTooltip', { year: shift.raveNewYear, gate: shift.current.gate }),
        }
        : null,
      untilTooltip: translate('activeUntil', {
        time: shift.untilUtc.clone().local().locale(locale).format(dateTimeFormats.dayAsWordWithTime),
      }),
      gateLinkText: translate('gateLink', { gate: shift.current.gate }),
      gateVm: gateVm(shift.current.gate),
      lineVm: lineVm(shift.current.gate, shift.current.line, shift.planetId),
      source: shift,
    };
  }

  private toStationVm(
    station: HdTransitDayStation,
    names: Readonly<Record<string, string>>,
    translate: (key: string, params?: Record<string, unknown>) => string,
  ): DayFlowStationVm {
    const config = getHdPlanetDisplayConfig(station.planetKey);
    return {
      type: 'station',
      id: station.id,
      timeText: moment(station.atMs).format('HH:mm'),
      planetName: names[station.planetKey] ?? config.label,
      glyph: config.astronomiconChar,
      color: config.stripeColor,
      text: translate(station.turnsTo === 'direct' ? 'stationDirect' : 'stationRetrograde'),
    };
  }

  private toSeasonVm(
    entry: HdTransitDaySeason,
    translate: (key: string, params?: Record<string, unknown>) => string,
  ): DayFlowSeasonVm {
    const season = entry.season;
    return {
      type: 'season',
      id: entry.id,
      timeText: moment(entry.atMs).format('HH:mm'),
      icon: hdSeasonBySunGate(season.sunGate)?.icon ?? 'sun',
      text: this.mandalaText(`seasons.${season.key}`),
      detail: translate('seasonDetail', {
        sunGateLine: formatGateLine(season.sunGate, season.sunLine),
        earthGateLine: formatGateLine(season.earthGate, season.sunLine),
      }),
    };
  }

  /** Kwartaal- en seizoensnamen delen we met het mandala-wiel (`humanDesign.chart.mandala.*`). */
  private mandalaText(key: string): string {
    return this.transloco.translate<string>(`humanDesign.chart.mandala.${key}`, undefined, this.lang());
  }

  // ── Scroll, anker en venster ─────────────────────────────────────────────

  private onAfterRender(): void {
    if (!this.contentDirty) return;
    this.contentDirty = false;
    const element = this.visibleScroller();
    if (element == null) return;
    this.visible = true;
    this.restoreAnchor(element);
    this.clearRailOverlaps(element);
    this.emitFocus();
    this.requestFrame();
  }

  /**
   * BELANGRIJK: de plusjes per wissel hangen links in de railkolom, ter hoogte
   * van het lijnnummer, en de sticky datumrail schuift er bij het scrollen
   * overheen (z-index). Bij de eerste wissel van een dag valt dat lijnnummer
   * in rust echter vaak nog naast de rail — die is sinds de dag-momentknop
   * onderaan erin zit hoger dan de dagkop + de eerste wisselkop — en dan zou
   * de rail dat plusje ook zonder scrollen bedekken. Zo'n plusje zakt net
   * onder de rust-onderkant van de rail (`--add-nudge`). Enkel absolute
   * knoppen verschuiven: geen layout-impact, dus het scroll-anker blijft
   * onaangeroerd. Spec: "laat een plusje dat in rust naast de rail valt
   * eronder zakken".
   */
  private clearRailOverlaps(element: HTMLElement): void {
    for (const section of Array.from(element.querySelectorAll<HTMLElement>('[data-day-key]'))) {
      const rail = section.querySelector<HTMLElement>('.day-flow__rail');
      if (rail == null) continue;
      // Rustpositie van de sticky rail: bovenaan de inhoud van de dag.
      const railBottom = section.clientTop
        + parseFloat(getComputedStyle(section).paddingTop)
        + rail.offsetHeight
        + RAIL_ADD_CLEARANCE_PX;
      const sectionTop = section.getBoundingClientRect().top;
      for (const button of Array.from(section.querySelectorAll<HTMLElement>('.day-flow__add--shift'))) {
        const currentNudge = parseFloat(button.style.getPropertyValue('--add-nudge')) || 0;
        const naturalTop = button.getBoundingClientRect().top - sectionTop - currentNudge;
        const nudge = Math.max(0, Math.ceil(railBottom - naturalTop));
        if (nudge !== currentNudge) {
          if (nudge > 0) button.style.setProperty('--add-nudge', `${nudge}px`);
          else button.style.removeProperty('--add-nudge');
        }
        // De volgende plusjes staan lager: die vallen al onder de rail.
        if (naturalTop >= railBottom) break;
      }
    }
  }

  /**
   * Zet de scroll op het anker. Een schrijfactie gebeurt enkel als de inhoud
   * boven het anker veranderde — en dat laat de treadmill tijdens het scrollen
   * niet toe. Geklemd (inhoud nog te kort): het anker blijft wat het was.
   */
  private restoreAnchor(element: HTMLElement): void {
    if (this.nowJumpPending) this.resolveNowAnchor(element);
    const current = this.anchor;
    const section = current == null ? null : this.sectionFor(current.key);
    if (current == null || section == null) return;
    // Een dag die (door minder planeten) korter werd dan de offset: toon zijn begin.
    const anchor = current.offset >= section.offsetHeight ? { key: current.key, offset: 0 } : current;
    this.anchor = anchor;
    const target = section.offsetTop + anchor.offset;
    if (Math.abs(element.scrollTop - target) >= 1) element.scrollTop = target;
    this.ownedScrollTop = element.scrollTop;
  }

  /**
   * Nu-sprong: het anker wordt de nu-lijn, op een derde van de hoogte van de
   * flow. BELANGRIJK: zolang vandaag nog laadt, kan er boven de nu-lijn nog
   * inhoud bijkomen (de maan komt vaak later binnen dan de zon) en zou een
   * eenmalig bepaald anker de lijn uit beeld duwen. De sprong blijft daarom
   * openstaan tot de dag volledig is; elke render bepaalt het anker opnieuw.
   * Een eigen scroll of een andere sprong beëindigt hem. Spec: "Nu-knop".
   */
  private resolveNowAnchor(element: HTMLElement): void {
    const key = this.anchor?.key;
    const section = key == null ? null : this.sectionFor(key);
    if (key == null || section == null) return;
    const line = section.querySelector<HTMLElement>('.day-flow__now');
    if (line != null) {
      const lineTop = line.getBoundingClientRect().top - section.getBoundingClientRect().top;
      const offset = Math.round(lineTop - element.clientHeight * NOW_JUMP_VIEWPORT_FRACTION);
      this.anchor = { key, offset: Math.max(0, offset) };
    }
    if (!section.classList.contains('day-flow__day--pending')) this.nowJumpPending = false;
  }

  /**
   * Scroll-event: de eigen scroll-zetten (herkenbaar aan de positie) tellen
   * niet, al de rest is de gebruiker.
   */
  private onScroll(element: HTMLElement): void {
    if (this.isOwnedPosition(element)) return;
    this.nowJumpPending = false;
    this.ownedScrollTop = null;
    this.activity.userScroll(Date.now());
    this.userScrollSinceFrame = true;
    this.requestFrame();
    this.scheduleIdleCheck();
  }

  /**
   * Een verborgen dock-tab (`display: none`) meet hoogte 0 en verliest zijn
   * scrollpositie: bij het terug tonen het anker herstellen. Een
   * hoogtewijziging tijdens het scrollen wacht tot het scrollen stilvalt.
   */
  private onResize(): void {
    const element = this.scroller()?.nativeElement;
    if (element == null) return;
    const height = element.clientHeight;
    if (height <= 0) {
      this.visible = false;
      this.lastClientHeight = 0;
      return;
    }
    const becameVisible = !this.visible;
    if (!becameVisible && !this.activity.isIdle(Date.now())) {
      this.resizePending = true;
      this.scheduleIdleCheck();
      return;
    }
    this.resizePending = false;
    this.visible = true;
    if (height !== this.lastClientHeight) {
      // Laad-placeholders zijn ongeveer een schermhoogte hoog (zie de SCSS).
      element.style.setProperty('--day-flow-viewport-px', `${height}px`);
      this.lastClientHeight = height;
    }
    this.restoreAnchor(element);
    // Een andere breedte laat wisselkoppen anders afbreken: plusjes opnieuw toetsen.
    this.clearRailOverlaps(element);
    this.emitFocus();
    this.requestFrame();
  }

  /** Eén frame per keer: eventueel het anker meten (de gebruiker scrolde), dan het venster toetsen. */
  private requestFrame(): void {
    if (this.frame != null) return;
    const view = this.view();
    if (view == null) return;
    this.ngZone.runOutsideAngular(() => {
      this.frame = view.requestAnimationFrame(() => {
        this.frame = null;
        if (this.userScrollSinceFrame) {
          this.userScrollSinceFrame = false;
          this.captureUserAnchor();
          this.emitFocus();
        }
        this.evaluateWindow();
      });
    });
  }

  /** Wacht tot het scrollen stilvalt en past dan wat bleef liggen toe. */
  private scheduleIdleCheck(): void {
    if (this.idleTimer != null) return;
    const delay = Math.max(1, this.activity.msUntilIdle(Date.now()));
    this.ngZone.runOutsideAngular(() => {
      this.idleTimer = setTimeout(() => {
        this.idleTimer = null;
        if (!this.activity.isIdle(Date.now())) {
          this.scheduleIdleCheck();
          return;
        }
        this.onIdle();
      }, delay);
    });
  }

  private onIdle(): void {
    const latest = untracked(this.planets);
    if (untracked(this.appliedPlanets) !== latest) this.appliedPlanets.set(latest);
    if (this.resizePending) this.onResize();
    this.requestFrame();
  }

  /**
   * BELANGRIJK: iOS Safari laat een lopende momentum-scroll gewoon doorlopen
   * na een scrollTop-zet — "Vandaag" landde na snel scrollen enkele dagen
   * verder (Stevens iPhone-test 2026-09-26). `overflow: hidden` breekt de
   * momentum af; een frame later mag er weer gescrold worden.
   */
  private stopMomentum(element: HTMLElement): void {
    const view = this.view();
    if (view == null) return;
    element.style.overflowY = 'hidden';
    if (this.momentumFrame != null) view.cancelAnimationFrame(this.momentumFrame);
    this.ngZone.runOutsideAngular(() => {
      this.momentumFrame = view.requestAnimationFrame(() => {
        this.momentumFrame = null;
        element.style.removeProperty('overflow-y');
      });
    });
  }

  private cancelScheduled(): void {
    const view = this.view();
    if (view != null) {
      if (this.frame != null) view.cancelAnimationFrame(this.frame);
      if (this.momentumFrame != null) view.cancelAnimationFrame(this.momentumFrame);
    }
    this.frame = null;
    this.momentumFrame = null;
    if (this.idleTimer != null) clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  /**
   * Eén venster-stap per frame; de volgende stap volgt na de render
   * (afterEveryRender → frame). Boven het beeld enkel als de gebruiker niet scrolt.
   */
  private evaluateWindow(): void {
    const window = this.window();
    const metrics = this.visible ? this.readMetrics() : null;
    if (window == null || metrics == null) return;
    const step = planHdTransitDayWindowStep(metrics, window, {
      bounds: DOMAIN_BOUNDS,
      aboveChangesAllowed: this.activity.isIdle(Date.now()),
    });
    if (step != null) this.window.set(applyHdTransitDayWindowStep(window, step));
  }

  /** De positie van de gebruiker wordt het anker — tenzij de scroller nog op de eigen zet staat. */
  private captureUserAnchor(): void {
    const element = this.visibleScroller();
    if (element == null || !this.visible || this.isOwnedPosition(element)) return;
    const metrics = this.readMetrics();
    const anchor = metrics == null ? null : this.anchorFromMetrics(metrics);
    if (anchor != null) this.anchor = anchor;
  }

  /** De focusdag (= de dag van het anker) melden aan de host als hij veranderde. */
  private emitFocus(): void {
    const key = this.anchor?.key;
    if (key == null || key === this.lastFocusKey) return;
    this.lastFocusKey = key;
    this.ngZone.run(() => this.focusDayChanged.emit(key));
  }

  private isOwnedPosition(element: HTMLElement): boolean {
    return this.ownedScrollTop != null && Math.abs(element.scrollTop - this.ownedScrollTop) < 1;
  }

  private anchorFromMetrics(metrics: HdTransitDayFlowMetrics): DayFlowAnchor | null {
    const key = hdTransitFocusDayKey(metrics);
    const day = metrics.days.find(candidate => candidate.key === key);
    return key == null || day == null ? null : { key, offset: metrics.scrollTop - day.top };
  }

  /** DOM-metingen van de scroller; `null` zolang hij niet zichtbaar is. */
  private readMetrics(): HdTransitDayFlowMetrics | null {
    const element = this.visibleScroller();
    if (element == null) return null;
    const sections = Array.from(element.querySelectorAll<HTMLElement>('[data-day-key]'));
    return {
      scrollTop: element.scrollTop,
      clientHeight: element.clientHeight,
      scrollHeight: element.scrollHeight,
      days: sections.map(section => ({
        key: section.dataset['dayKey'] ?? '',
        top: section.offsetTop,
        height: section.offsetHeight,
      })),
    };
  }

  private visibleScroller(): HTMLElement | null {
    const element = this.scroller()?.nativeElement;
    return element != null && element.clientHeight > 0 ? element : null;
  }

  private sectionFor(dayKey: string): HTMLElement | null {
    return this.scroller()?.nativeElement.querySelector<HTMLElement>(`[data-day-key="${dayKey}"]`) ?? null;
  }

  private view(): Window | null {
    return this.hostElement.nativeElement.ownerDocument.defaultView;
  }
}

/** Poort (en lijn, als de granulariteit die kent) van het segment dat `atMs` bevat. */
function gatePointAt(segments: readonly HdTransitSegment[], atMs: number): HdIChingGatePoint | undefined {
  const segment = segments.find(s => s.fromUtc.valueOf() <= atMs && atMs < s.toUtc.valueOf());
  if (!segment) return undefined;
  return segment.line == null ? { gate: segment.gate } : { gate: segment.gate, line: segment.line as IChingLinePosition };
}
